/**
 * The bar above the Users table while people are ticked (SF-4875): give them all a team, or
 * add a role to (or, SF-5076, remove a role from) each of them. Only roles that apply to every company are offered here;
 * a customer role needs its own company list, which stays a per-person edit.
 */
import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Popconfirm, Select, Space, Typography, message } from 'antd';
import { TEAM_OPTIONS } from '../../../utils/teams';
import type { AuthRole } from '../utils';
import {
  NO_TEAM,
  addRoleToUsers,
  removeRoleFromUsers,
  setTeamForUsers,
  type BatchResult,
} from '../batchUpdate';

const BATCH_TEAM_OPTIONS = [...TEAM_OPTIONS, { value: NO_TEAM, label: 'No team' }];

interface UsersBatchBarProps {
  /** Ticked people, in the order they were ticked. */
  selectedIds: string[];
  /** Name or email per id, for the list of people a change failed for. */
  labelFor: (id: string) => string;
  roles: AuthRole[];
  rolesLoading: boolean;
  /** The role list could not be loaded: say so instead of "No roles", and allow no role add. */
  rolesFailed: boolean;
  /** Called after any batch that changed someone, so the list can reload. */
  onChanged: () => void;
  onClear: () => void;
}

interface Report {
  action: string;
  result: BatchResult;
  /** Names taken when the batch started, so clearing the ticks keeps them. */
  labels: Map<string, string>;
  unchangedNote: string;
}

interface BatchRun {
  action: string;
  /** How people the batch left alone are described, e.g. "already had it". */
  unchangedNote: string;
  ids: string[];
  labels: Map<string, string>;
  batch: (ids: string[]) => Promise<BatchResult>;
}

const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

export const UsersBatchBar: React.FC<UsersBatchBarProps> = ({
  selectedIds,
  labelFor,
  roles,
  rolesLoading,
  rolesFailed,
  onChanged,
  onClear,
}) => {
  const [team, setTeam] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  // Set at once on confirm, before the mutation state re-renders, so a second confirm can't
  // start an overlapping run.
  const busy = useRef(false);

  const roleOptions = roles
    .filter((r) => r.scope !== 'customer')
    .map((r) => ({ value: r.name, label: r.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const count = selectedIds.length;

  // runBatch never rejects (each person's failure is in the result), so there is no onError.
  const mutation = useMutation({
    mutationFn: ({ ids, batch }: BatchRun) => batch(ids),
    onSuccess: (result, { action, labels, unchangedNote }) => {
      if (result.updated.length > 0) onChanged();
      if (result.failed.length === 0) {
        const skipped = result.unchanged.length
          ? `, ${people(result.unchanged.length)} ${unchangedNote}`
          : '';
        message.success(`${action}: ${people(result.updated.length)} updated${skipped}`);
      } else {
        setReport({ action, result, labels, unchangedNote });
      }
    },
    onSettled: () => {
      busy.current = false;
    },
  });
  const running = mutation.isLoading;

  const start = (action: string, batch: BatchRun['batch'], unchangedNote = 'already had it') => {
    if (busy.current || selectedIds.length === 0) return;
    busy.current = true;
    setReport(null);
    // Copy the ids and names: the selection can change while the batch runs.
    const ids = [...selectedIds];
    const labels = new Map(ids.map((id) => [id, labelFor(id)] as const));
    mutation.mutate({ action, ids, labels, batch, unchangedNote });
  };

  const applyTeam = () => {
    if (!team) return;
    const label = BATCH_TEAM_OPTIONS.find((o) => o.value === team)?.label ?? team;
    start(`Team set to ${label}`, (ids) => setTeamForUsers(ids, team));
  };

  const applyRole = () => {
    if (!role || !roleOptions.some((o) => o.value === role)) return;
    start(`Role ${role} added`, (ids) => addRoleToUsers(ids, role));
  };

  const removeRole = () => {
    if (!role || !roleOptions.some((o) => o.value === role)) return;
    start(`Role ${role} removed`, (ids) => removeRoleFromUsers(ids, role), "didn't have it");
  };

  const roleUnavailable = rolesLoading || rolesFailed;

  // Stays mounted while a batch runs or its report shows, even if every tick is cleared.
  if (count === 0 && !running && !report) return null;

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space wrap align="center" aria-label="Batch actions">
        <Typography.Text strong>{people(count)} selected</Typography.Text>
        <Select
          aria-label="Team for selected people"
          placeholder="Choose a team"
          options={BATCH_TEAM_OPTIONS}
          value={team}
          onChange={setTeam}
          disabled={running}
          style={{ width: 200 }}
        />
        <Popconfirm
          title={`Set the team for ${people(count)}?`}
          onConfirm={applyTeam}
          okText="Set team"
          disabled={!team || running || count === 0}
        >
          <Button disabled={!team || running || count === 0} loading={running}>
            Set team
          </Button>
        </Popconfirm>
        <Select
          aria-label="Role for selected people"
          placeholder="Choose a role"
          options={roleOptions}
          value={role}
          onChange={setRole}
          showSearch
          optionFilterProp="label"
          loading={rolesLoading}
          disabled={running}
          notFoundContent={
            rolesLoading ? 'Loading roles…' : rolesFailed ? 'Could not load roles' : 'No roles'
          }
          style={{ width: 220 }}
        />
        <Popconfirm
          title={`Add this role to ${people(count)}?`}
          description="Their other roles are kept."
          onConfirm={applyRole}
          okText="Add role"
          disabled={!role || running || count === 0 || roleUnavailable}
        >
          <Button disabled={!role || running || count === 0 || roleUnavailable} loading={running}>
            Add role
          </Button>
        </Popconfirm>
        <Popconfirm
          title={`Remove this role from ${people(count)}?`}
          description="Only this role is taken away. Their other roles are kept, and anyone without it is left as they are."
          onConfirm={removeRole}
          okText="Remove role"
          okButtonProps={{ danger: true }}
          disabled={!role || running || count === 0 || roleUnavailable}
        >
          <Button
            danger
            disabled={!role || running || count === 0 || roleUnavailable}
            loading={running}
          >
            Remove role
          </Button>
        </Popconfirm>
        <Button type="link" onClick={onClear} disabled={running}>
          Clear selection
        </Button>
      </Space>
      {report && (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setReport(null)}
          message={`${report.action}: ${people(report.result.updated.length)} updated, ${
            report.result.unchanged.length
              ? `${people(report.result.unchanged.length)} ${report.unchangedNote}, `
              : ''
          }${people(report.result.failed.length)} failed`}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {report.result.failed.map((f) => (
                <li key={f.id}>
                  {report.labels.get(f.id) ?? f.id}: {f.message}
                </li>
              ))}
            </ul>
          }
        />
      )}
    </Space>
  );
};
